import { useEffect, useState, useCallback } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  mainSection,
  helloText,
  subTextSection,
  pinPromptText,
  pinInputBox,
  pinInputField,
  helperTextBox,
  helperText,
  InvalidText,
  pinTextFieldWrapper,
  pinErrorText,
} from "./landingPageStyles";
import { useGetFastrLinkConfigurationQuery } from "@services/fastrAppLinks";
import PageLayout from "@components/pageLayout/PageLayout";
import { localStorageService } from "@utils/storageService/StorageService";
import useAuth from "@features/auth/useAuth";
import { useOnVisibilityAndInterval } from "@hooks/useOnVisibilityAndInterval";

const LOCK_DURATION_MS = 2 * 60 * 1000;
const MAX_ATTEMPTS = 3;
const ATTEMPTS_KEY = "fastr_failed_attempts";
const LOCKOUT_KEY = "fastr_locked_until";

const CHECK_IF_LINK_IS_STILL_VALID_INTERVAL_MS = 60 * 1000; // 1 minute

const LandingPage = () => {
  const navigate = useNavigate();

  const {
    practiceId,
    fastrId: fastrLinkId,
    fastrHash: fastrLinkHash,
  } = useParams<{
    practiceId: string;
    fastrId: string;
    fastrHash: string;
  }>();

  useEffect(() => {
    if (practiceId && fastrLinkId && fastrLinkHash) {
      // These values are stored in localStorage to allow auto navigating
      // to the pin page after a session ends.
      localStorageService.set("fastr_practiceId", practiceId);
      localStorageService.set("fastr_fastrId", fastrLinkId);
      localStorageService.set("fastr_fastrHash", fastrLinkHash);
    }
  }, [fastrLinkHash, fastrLinkId, practiceId]);

  const {
    data,
    error,
    isLoading,
    isError: isQueryError,
    refetch,
  } = useGetFastrLinkConfigurationQuery(
    {
      practiceId: practiceId || "",
      fastrLinkId: fastrLinkId || "",
      fastrLinkHash: fastrLinkHash || "",
    },
    {
      skip: !practiceId || !fastrLinkId || !fastrLinkHash,
    }
  );

  const httpStatusCode =
    (error as any)?.status ||
    (error as any)?.originalStatus ||
    data?.httpStatus ||
    null;

  const getErrorMessages = (): string[] => {
    // Check for missing parameters first
    if (!practiceId || !fastrLinkId || !fastrLinkHash) {
      return ["Invalid link format", "Please check the URL and try again."];
    }

    if (isLoading) {
      return [];
    }

    if (httpStatusCode === 404) {
      return [
        "This link is no longer valid",
        "Contact your admin for a new one.",
      ];
    }

    if (isQueryError && !httpStatusCode) {
      return [
        "Unable to load the page",
        "Please check your connection and try again.",
      ];
    }

    if (httpStatusCode && httpStatusCode >= 400 && httpStatusCode !== 404) {
      return [
        `Invalid url: Unable to load the page`,
        "Please try again or contact your admin.",
      ];
    }

    return [];
  };

  const errorMessages = getErrorMessages();
  const hasError = errorMessages.length > 0;
  const shouldShowPinForm =
    !isLoading &&
    practiceId &&
    fastrLinkId &&
    fastrLinkHash &&
    httpStatusCode === 204;

  const checkIfLinkIsStillValid = useCallback(() => {
    if (hasError || isLoading) {
      return;
    }
    refetch();
  }, [hasError, isLoading, refetch]);
  useOnVisibilityAndInterval({
    callback: checkIfLinkIsStillValid,
    intervalMs: CHECK_IF_LINK_IS_STILL_VALID_INTERVAL_MS,
    runOnEffectFire: false,
  });

  const [login, isLoginLoading] = useAuth().login;

  const [pin, setPin] = useState("");
  const [isError, setIsError] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const lockedUntil = Number(localStorageService.get(LOCKOUT_KEY));
    if (lockedUntil && Date.now() < lockedUntil) {
      setIsLocked(true);
      const remaining = lockedUntil - Date.now();
      setTimeout(() => {
        setIsLocked(false);
        localStorageService.remove(LOCKOUT_KEY);
      }, remaining);
    }
  }, []);

  const handleSubmitPin = async () => {
    const failedAttempts = Number(localStorageService.get(ATTEMPTS_KEY)) || 0;
    const lockedUntil = Number(localStorageService.get(LOCKOUT_KEY)) || 0;

    if (lockedUntil && Date.now() < lockedUntil) {
      setIsLocked(true);
      setIsError(true);
      return;
    }

    try {
      await login({ pin });

      localStorageService.remove(ATTEMPTS_KEY);
      localStorageService.remove(LOCKOUT_KEY);

      setIsError(false);
      setIsLocked(false);
      navigate("/form-assignments");
    } catch (err) {
      console.error("Login error:", err);

      const newAttempts = failedAttempts + 1;
      localStorageService.set(ATTEMPTS_KEY, newAttempts.toString());

      if (newAttempts >= MAX_ATTEMPTS) {
        const lockUntil = Date.now() + LOCK_DURATION_MS;
        localStorageService.set(LOCKOUT_KEY, lockUntil.toString());
        localStorageService.remove(ATTEMPTS_KEY);
        setIsLocked(true);
        setTimeout(() => {
          localStorageService.remove(LOCKOUT_KEY);
          localStorageService.remove(ATTEMPTS_KEY);
          setIsLocked(false);
        }, LOCK_DURATION_MS);
      }

      setIsError(true);
      setPin("");
    }
  };

  return (
    <PageLayout
      primaryButtonConfig={
        shouldShowPinForm
          ? {
              label: "Assign Forms",
              disabled: pin.length !== 6 || isLoginLoading || isLocked,
              onClick: handleSubmitPin,
            }
          : undefined
      }
    >
      {hasError && (
        <Box sx={helperTextBox}>
          {errorMessages.map((message, index) => (
            <Typography key={index} sx={InvalidText}>
              {message}
            </Typography>
          ))}
        </Box>
      )}
      {isLoading && (
        <Box sx={helperTextBox}>
          <Typography sx={helperText}>{"Loading..."}</Typography>
        </Box>
      )}

      {shouldShowPinForm && (
        <>
          <Box sx={mainSection}>
            <Typography sx={helloText}>Hello!</Typography>
            {isError && (
              <Typography sx={pinErrorText}>
                {isLocked
                  ? "You've entered an incorrect PIN 3 times, try again in 2 minutes."
                  : "The PIN entered is incorrect. Please try again."}
              </Typography>
            )}
            <Box sx={subTextSection}>
              <Typography sx={pinPromptText}>
                {"Enter your team's 6-digit PIN"}
              </Typography>
              <Box sx={pinInputBox}>
                <TextField
                  aria-label="pin entry"
                  variant="standard"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      pin.length === 6 &&
                      !isLoginLoading &&
                      !isLocked
                    ) {
                      handleSubmitPin();
                    }
                  }}
                  placeholder="Enter PIN"
                  slotProps={{
                    input: {
                      sx: pinInputField(pin.length),
                      inputMode: "numeric",
                      inputProps: {
                        maxLength: 6,
                      },
                    },
                  }}
                  sx={pinTextFieldWrapper}
                />
              </Box>
            </Box>
            <Box sx={helperTextBox}>
              <Typography sx={helperText}>
                {"Don't remember the pin?"}
              </Typography>
              <Typography sx={helperText}>
                {
                  "Ask your office's Leap Admin. They can check Leap's Settings for the PIN."
                }
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </PageLayout>
  );
};

export default LandingPage;
